"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../css/Clinic.module.css";
import ScheduleConsult from "../components/ScheduleConsult.jsx";
import TodaysAppointments from "../components/TodaysAppointments.jsx";
import SendReminder from "../components/SendReminder.jsx";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Popup from "../../../../components/Popup.jsx";
import { getAppointments as fetchAppointmentsApi, updateAppointmentStatus as updateAppointmentStatusApi } from "../../../../api/clinic/bookingApi.js";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

// Helper to safely extract YYYY-MM-DD from various date formats
const extractDateISO = (val) => {
  if (!val) return "";
  try {
    // If it's already a string in YYYY-MM-DD format
    const strVal = String(val);
    if (/^\d{4}-\d{2}-\d{2}$/.test(strVal)) return strVal;
    // Try parsing as Date
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Fallback: extract first 10 chars if it looks like ISO
    if (strVal.length >= 10 && strVal.includes('-')) {
      return strVal.slice(0, 10);
    }
  } catch (e) {
    console.error('[extractDateISO] Failed to parse:', val, e);
  }
  return "";
};

// Helper to extract HH:mm:ss from various time formats
const extractTimeHMS = (val) => {
  if (!val) return "";
  try {
    const strVal = String(val);
    // If it's an ISO datetime string (e.g., "1970-01-01T08:40:00.000Z")
    if (strVal.includes('T')) {
      const d = new Date(strVal);
      if (!isNaN(d.getTime())) {
        const h = String(d.getUTCHours()).padStart(2, '0');
        const m = String(d.getUTCMinutes()).padStart(2, '0');
        const s = String(d.getUTCSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
      }
    }
    // If it's already in HH:mm:ss or HH:mm format
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(strVal)) {
      const parts = strVal.split(':');
      const h = parts[0].padStart(2, '0');
      const m = parts[1].padStart(2, '0');
      const s = (parts[2] || '00').padStart(2, '0');
      return `${h}:${m}:${s}`;
    }
  } catch (e) {
    console.error('[extractTimeHMS] Failed to parse:', val, e);
  }
  return "";
};

export default function Clinic() {
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [reminderFor, setReminderFor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchAppointmentsApi();
        const mapped = Array.isArray(data)
          ? data.map((a) => {
              let dateIso = extractDateISO(a.preferreddate);
              if (!dateIso || dateIso === "1970-01-01") {
                const ca = a.createdat || a.createdAt;
                if (ca) dateIso = extractDateISO(ca);
              }
              const timeStr = extractTimeHMS(a.preferredtime);
              const name = `${a.patient?.firstname || ""} ${a.patient?.surname || ""}`.trim();
              return {
                id: a.appointmentid ?? a.id ?? crypto.randomUUID(),
                patient: { name, phone: a.patient?.contactnumber || "", email: a.patient?.email || "" },
                serviceDetail: "",
                date: dateIso,
                time: timeStr,
                status: a.status || "Pending",
                createdAt: a.createdat || a.createdAt || "",
              };
            })
          : [];
        if (!cancelled) setAppointments(mapped);
      } catch {
        if (!cancelled) setAppointments([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [API_BASE]);

  // Mock patients and services. Replace with API later.
  const patients = useMemo(
    () => [
      { id: "p1", name: "John Doe", phone: "060 856 8569", email: "johndoe@gmail.com" },
      { id: "p2", name: "Jennifer King", phone: "060 123 4567", email: "jking@gmail.com" },
      { id: "p3", name: "Mike White", phone: "060 765 4321", email: "mwhite@gmail.com" },
      { id: "p4", name: "Natalia Lopez", phone: "060 111 2222", email: "natalia@gmail.com" },
    ],
    []
  );

  const serviceOptions = useMemo(
    () => ({
      "Family Planning": null,
      "Consultation": null,
      "Wound Care": ["Dressing", "Suture Check"],
      "Injection": ["Vitamin D", "B12", "Tetanus", "Flu", "Other"],
    }),
    []
  );

  const addAppointment = (appt) => {
    setAppointments((prev) => [{ ...appt, id: crypto.randomUUID(), status: "Pending" }, ...prev]);
    setSuccessMsg("Appointment booked successfully.");
  };

  const cancelAppointment = async (id) => {
    try {
      await updateAppointmentStatusApi(id, "Cancelled");
      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: "Cancelled" } : a))
      );
      setSuccessMsg("Appointment cancelled.");
    } catch {}
  };
  const todayISO = new Date().toLocaleDateString("en-CA");
  const todaysAppointments = useMemo(() => {
    const isTodayLocal = (dateStr) => {
      if (!dateStr) return false;
      // Direct string comparison for YYYY-MM-DD format
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr === todayISO;
      }
      // Fallback for other formats
      const datePart = String(dateStr).slice(0, 10);
      return datePart === todayISO;
    };
    // Filter by preferred appointment date (a.date), not creation date
    return appointments
      .filter((a) => isTodayLocal(a.date))
      .filter((a) => {
        const status = (a.status || "Pending").toLowerCase();
        return status === "pending" || status === "cancelled";
      });
  }, [appointments, todayISO]);

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.container}>
        <h2 className={styles.pageTitle}>Booking System</h2>
        <div className={styles.grid}>
          <div className={styles.leftPane}>
            <div className={styles.card}>
              <h3 className={styles.sectionTitle}>Schedule Consultation</h3>
              <ScheduleConsult
                patients={patients}
                serviceOptions={serviceOptions}
                onBook={addAppointment}
              />
            </div>
          </div>
          <div className={styles.rightPane}>
            <TodaysAppointments
              appointments={todaysAppointments}
              onCancel={cancelAppointment}
              onSendReminder={(appt) => setReminderFor(appt)}
              onViewAll={() => navigate("/customer/clinic/booking/appointments")}
            />
          </div>
        </div>

        {successMsg && (
          <Popup type="success" message={successMsg} onClose={() => setSuccessMsg("")} />
        )}

        {reminderFor && (
          <SendReminder
            appointment={reminderFor}
            onClose={() => setReminderFor(null)}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
